import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { ClockIcon } from "lucide-react";
import { format } from "date-fns";

import { DirectTimeEntryForm } from "./DirectTimeEntryForm";
import { CompletedWorkStepDisplay } from "./CompletedWorkStepDisplay";
import { StartWorkStepForm, PauseCompleteWorkStepForm, ResumeCompleteWorkStepForm } from "../forms/WorkStepForms";

export function WorkStepTimeTracker({ assignment, onStart, onPause, onComplete, onCompleteDirect, onAddDirectTime, onFetchTimeEntries, hasActiveWorkStep, canStart = true }) {
  const [timeEntries, setTimeEntries] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDirectEntry, setShowDirectEntry] = useState(false);
  const [currentStartTime, setCurrentStartTime] = useState(format(new Date(), "HH:mm"));

  const loadTimeEntries = async () => {
    const entries = await onFetchTimeEntries(assignment.id);
    setTimeEntries(entries);
  };

  useEffect(() => {
    if (showDialog || showDirectEntry) {
      setCurrentStartTime(format(new Date(), "HH:mm"));
      loadTimeEntries();
    }
  }, [showDialog, showDirectEntry, assignment.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStartTime(format(new Date(), "HH:mm"));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleStart = async (startData) => {
    await onStart(assignment.id, startData);
    setShowDialog(false);
    setCurrentStartTime(format(new Date(), "HH:mm"));
  };

  const handlePause = async (pauseData) => {
    await onPause(assignment.id, pauseData);
    await loadTimeEntries();
  };

  const handleComplete = async (completionData) => {
    await onComplete(assignment.id, completionData);
    setShowDialog(false);
  };

  const handleCompleteDirect = async (directTimeData) => {
    await onCompleteDirect(assignment.id, directTimeData);
    setShowDirectEntry(false);
  };

  const handleAddDirectTime = async (directTimeData) => {
    await onAddDirectTime(assignment.id, directTimeData);
    await loadTimeEntries();
  };

  const getTotalMinutes = () => {
    return timeEntries.reduce((total, entry) => {
      if (entry.start_time && entry.end_time) {
        const start = new Date(entry.start_time);
        const end = new Date(entry.end_time);
        return total + Math.round((end - start) / (1000 * 60));
      }
      return total;
    }, 0);
  };

  const getCurrentSessionStart = () => {
    const activeEntry = timeEntries.find(entry => entry.start_time && !entry.end_time);
    return activeEntry ? format(new Date(activeEntry.start_time), "HH:mm") : null;
  };

  // Check if user can start this work step (no other active work step AND previous step completed)
  const canStartWorkStepNow = () => {
    const noOtherActive = !hasActiveWorkStep || hasActiveWorkStep.id === assignment.id;
    return noOtherActive && canStart;
  };

  const isLaserWorkStep = () => {
    return assignment.work_step?.step_type === 'lasern';
  };

  return (
    <>
      {assignment.status === 'Zugewiesen' && (
        <div className="flex flex-col space-y-2">
          {!canStart && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
              ⚠️ Der vorherige Arbeitsschritt muss zuerst abgeschlossen werden.
            </div>
          )}
          <div className="flex space-x-2">
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (open) {
              setCurrentStartTime(format(new Date(), "HH:mm"));
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className={canStartWorkStepNow() ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}
                disabled={!canStartWorkStepNow()}
              >
                <ClockIcon className="h-4 w-4 mr-2" />
                {canStartWorkStepNow() ? "Beginnen" : "Blockiert"}
              </Button>
            </DialogTrigger>
            {canStartWorkStepNow() && (
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Arbeitsschritt beginnen</DialogTitle>
                  <DialogDescription>
                    {assignment.work_step?.step_type || 'Arbeitsschritt'} für {assignment.task?.title}
                  </DialogDescription>
                </DialogHeader>
                <StartWorkStepForm
                  assignment={assignment}
                  defaultStartTime={currentStartTime}
                  onStart={handleStart}
                  onCancel={() => setShowDialog(false)}
                />
              </DialogContent>
            )}
          </Dialog>

          {isLaserWorkStep() && canStartWorkStepNow() && (
            <Dialog open={showDirectEntry} onOpenChange={setShowDirectEntry}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                >
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Nettozeit eintragen
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Direkte Zeiteingabe - Lasern</DialogTitle>
                  <DialogDescription>
                    Tragen Sie Nettoarbeitszeit direkt ein - mehrere Einträge möglich
                  </DialogDescription>
                </DialogHeader>
                <DirectTimeEntryForm
                  assignment={assignment}
                  onComplete={handleCompleteDirect}
                  onAddTime={handleAddDirectTime}
                  onCancel={() => setShowDirectEntry(false)}
                  onLoadEntries={loadTimeEntries}
                  existingEntries={timeEntries}
                />
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>
      )}
      
      {hasActiveWorkStep && hasActiveWorkStep.id !== assignment.id && (
        <div className="text-xs text-orange-600 mt-1">
          Aktiv: {hasActiveWorkStep.work_step?.step_type} in {hasActiveWorkStep.task?.title}
        </div>
      )}

      {assignment.status === 'In Bearbeitung' && (
        <>
          {isLaserWorkStep() ? (
            <Dialog open={showDirectEntry} onOpenChange={(open) => {
              setShowDirectEntry(open);
              if (open) {
                setCurrentStartTime(format(new Date(), "HH:mm"));
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Weitere Nettozeit/Beenden
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Weitere Zeiteingabe - Lasern</DialogTitle>
                  <DialogDescription>
                    Fügen Sie weitere Nettoarbeitszeit hinzu oder schließen Sie den Arbeitsschritt ab
                  </DialogDescription>
                </DialogHeader>
                <DirectTimeEntryForm
                  assignment={assignment}
                  onComplete={handleCompleteDirect}
                  onAddTime={handleAddDirectTime}
                  onCancel={() => setShowDirectEntry(false)}
                  onLoadEntries={loadTimeEntries}
                  existingEntries={timeEntries}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (open) {
                setCurrentStartTime(format(new Date(), "HH:mm"));
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Pausieren/Beenden
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Arbeitsschritt: {assignment.work_step?.step_type}</DialogTitle>
                  <DialogDescription>
                    Pausieren Sie den Arbeitsschritt oder schließen Sie ihn komplett ab.
                  </DialogDescription>
                </DialogHeader>
                <PauseCompleteWorkStepForm 
                  assignment={assignment}
                  timeEntries={timeEntries}
                  currentSessionStart={getCurrentSessionStart()}
                  totalMinutes={getTotalMinutes()}
                  onPause={handlePause}
                  onComplete={handleComplete}
                  onCancel={() => setShowDialog(false)}
                />
              </DialogContent>
            </Dialog>
          )}
        </>
      )}

      {assignment.status === 'Pausiert' && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700">
              <ClockIcon className="h-4 w-4 mr-2" />
              Fortsetzen/Beenden
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Arbeitsschritt fortsetzen: {assignment.work_step?.step_type}</DialogTitle>
              <DialogDescription>
                Setzen Sie den Arbeitsschritt fort oder schließen Sie ihn ab.
              </DialogDescription>
            </DialogHeader>
            <ResumeCompleteWorkStepForm 
              assignment={assignment}
              timeEntries={timeEntries}
              totalMinutes={getTotalMinutes()}
              currentStartTime={currentStartTime}
              setCurrentStartTime={setCurrentStartTime}
              onStart={handleStart}
              onComplete={handleComplete}
              onCancel={() => setShowDialog(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {assignment.completed_at && (
        <CompletedWorkStepDisplay 
          assignment={assignment}
          timeEntries={timeEntries}
          totalMinutes={getTotalMinutes()}
          onLoad={() => loadTimeEntries()}
        />
      )}
    </>
  );
}

export default WorkStepTimeTracker;
